import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Form,
  Table,
  Pagination,
  InputGroup,
  Button,
} from "react-bootstrap";
import { getLevelChipClass } from "../utils/levelColors";

const API_URL = process.env.REACT_APP_API_URL;

const TeamStats = () => {
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [teamsPerPage] = useState(10);
  const [totalTeams, setTotalTeams] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await axios.get(`${API_URL}/seasons`);
        setSeasons(response.data);
        if (response.data.length > 0) {
          setSelectedSeason(response.data[0].id);
        }
      } catch (err) {
        console.error("Error fetching seasons:", err);
        setError("Failed to load seasons.");
      } finally {
        setLoading(false);
      }
    };
    fetchSeasons();
  }, []);

  useEffect(() => {
    const fetchTeams = async () => {
      if (!selectedSeason) return;
      setLoading(true);
      try {
        const countResponse = await axios.get(
          `${API_URL}/teams/count?season_id=${selectedSeason}&searchTerm=${searchTerm}`,
        );
        setTotalTeams(countResponse.data.total);

        const response = await axios.get(
          `${API_URL}/teams?season_id=${selectedSeason}&page=${currentPage}&limit=${teamsPerPage}&searchTerm=${searchTerm}`,
        );
        setTeams(response.data);
      } catch (err) {
        console.error("Error fetching teams:", err);
        setError("Failed to load teams data.");
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, [selectedSeason, currentPage, teamsPerPage, searchTerm]);


  const totalPages = Math.ceil(totalTeams / teamsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPaginationItems = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    return items;
  };

  if (loading && !selectedSeason) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Đang tải...</span>
        </Spinner>
        <p className="mt-3">Đang tải danh sách mùa giải...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger" className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          Không thể tải dữ liệu đội.
        </Alert>
      </Container>
    );
  }

  const selectedSeasonName =
    seasons.find((s) => s.id === parseInt(selectedSeason))?.name || "";

  return (
    <Container className="py-4">
      <h1 className="mb-4 fw-bold">
        Thống Kê Đội Bóng
      </h1>

      {/* Header Cards */}
      <Row className="mb-4 g-3">
        <Col md={4}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Tổng Số Đội</p>
              <h2 className="fw-bold mb-1">
                {totalTeams}
              </h2>
              <small className="text-muted">{selectedSeasonName}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Đội Hiển Thị</p>
              <h2 className="fw-bold mb-1">
                {teams.length}
              </h2>
              <small className="text-muted">Trang {currentPage}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Phân Trang</p>
              <h2 className="fw-bold mb-1">
                {totalPages}
              </h2>
              <small className="text-muted">{teamsPerPage} đội / Trang</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters Card */}
      <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <Card.Body className="p-4">
          <Row className="g-3">
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label className="text-muted small text-uppercase fw-bold mb-2">
                  Mùa Giải
                </Form.Label>
                <Form.Select
                  value={selectedSeason}
                  onChange={(e) => {
                    setSelectedSeason(e.target.value);
                    setCurrentPage(1);
                    setSearchTerm("");
                  }}
                  style={{ borderRadius: '8px' }}
                >
                  {seasons.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label className="text-muted small text-uppercase fw-bold mb-2">
                  Tìm Kiếm
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Tìm theo tên đội, CLB, địa chỉ..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ borderRadius: '8px 0 0 8px' }}
                  />
                  {searchTerm && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearchTerm("")}
                      style={{ borderRadius: '0 8px 8px 0' }}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Teams Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" className="mb-3" />
              <p>Đang tải danh sách đội...</p>
            </div>
          ) : teams.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="bg-white border-bottom">
                    <tr>
                      <th className="text-center" style={{ width: "60px" }}>
                        #
                      </th>
                      <th style={{ width: "20%" }}>Tên Đội</th>
                      <th style={{ width: "20%" }}>CLB Chủ Quản</th>
                      <th style={{ width: "40%" }}>Sân Vận Động</th>
                      <th className="text-center" style={{ width: "20%" }}>
                        Hạng Đấu
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team, index) => (
                      <tr key={team.id} className="align-middle">
                        <td className="text-center fw-bold text-muted">
                          {(currentPage - 1) * teamsPerPage + index + 1}
                        </td>
                        <td>
                          <Link
                            to={`/teams/${team.id}`}
                            style={{
                              textDecoration: 'none',
                              fontWeight: '600',
                              color: 'inherit',
                              transition: 'color 0.2s ease',
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-color)'}
                            onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                          >
                            {team.logo && (
                              <img
                                src={team.logo}
                                alt={team.name}
                                style={{
                                  width: "35px",
                                  height: "35px",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                  marginRight: "12px",
                                  border: "2px solid var(--accent-color)",
                                }}
                              />
                            )}
                            {team.name}
                          </Link>
                        </td>
                        <td className="fw-semibold">{team.club}</td>
                        <td>
                          <small className="text-muted">
                            {team.stadium}
                          </small>
                        </td>
                        <td className="text-center">
                          <span
                            className={`${getLevelChipClass(team.level_team_name)} level-chip-lg`}
                            style={{ minWidth: "80px" }}
                          >
                            {team.level_team_name}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-4 border-top">
                  <small className="text-muted">
                    Hiển thị{" "}
                    <strong className="text-primary">
                      {(currentPage - 1) * teamsPerPage + 1}
                    </strong>{" "}
                    đến{" "}
                    <strong className="text-primary">
                      {Math.min(currentPage * teamsPerPage, totalTeams)}
                    </strong>{" "}
                    trong tổng số{" "}
                    <strong className="text-primary">{totalTeams}</strong> đội
                  </small>
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {getPaginationItems()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <Alert variant="info" className="m-4 border-0" style={{ borderRadius: '12px' }}>
              {searchTerm
                ? `Không tìm thấy đội nào phù hợp với "${searchTerm}"`
                : "Không tìm thấy đội nào cho mùa giải này."}
            </Alert>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TeamStats;
